import React from 'react';
import { Form, Input, Button, message, Typography, Divider, Alert, Modal, Space, Popover } from 'antd';
import { UserOutlined, LockOutlined, GithubOutlined, GoogleOutlined, WechatOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { login } from '../services/authService';
import styled from 'styled-components';

const { Title } = Typography;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #1a365d 0%, #153e75 50%, #2a4365 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
    background-size: 30px 30px;
    opacity: 0.5;
    animation: backgroundMove 60s linear infinite;
  }

  @keyframes backgroundMove {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(-50%, -50%);
    }
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  padding: 20px 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 80%;
  max-width: 1200px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: cardAppear 0.6s ease-out;

  @keyframes cardAppear {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    width: 90%;
    flex-direction: column-reverse;
  }
`;

const LoginSection = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ImageSection = styled.div`
  flex: 1.2;
  background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),
              url('/chess-promo.jpg') center/cover;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 40px;
  color: white;
  position: relative;

  @media (max-width: 768px) {
    min-height: 200px;
  }
`;

const PromoText = styled.div`
  z-index: 1;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

  h2 {
    color: white;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    opacity: 0.9;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 24px;
  }

  .ant-input-affix-wrapper {
    padding: 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;

    &:hover, &:focus {
      border-color: #4299e1;
      box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.2);
    }
  }

  .ant-btn {
    height: 45px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(66, 153, 225, 0.4);
    }
  }
` as typeof Form;

const SocialLogin = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;

  .social-icon {
    font-size: 24px;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.2);
      color: #4299e1;
    }
  }
`;

const Footer = styled.div`
  text-align: center;
  padding: 0;
  color: #fff;
  position: relative;
  z-index: 1;

  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 16px;
    position: relative;
    z-index: 1;
    box-sizing: border-box;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .footer-link {
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
    transition: all 0.3s ease;
    position: relative;
    padding: 4px 0;
  }

  .footer-link:hover {
    color: #fff;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
  }
`;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleLogin = async (values: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await login(values.username, values.password);

      if (response.code === 'CONFIRM_REQUIRED') {
        Modal.confirm({
          title: '确认登录',
          content: response.message,
          okText: '确认登录',
          cancelText: '取消',
          onOk: async () => {
            try {
              // 强制登录
              const forceLoginResponse = await login(values.username, values.password, true);
              if (forceLoginResponse.code === 200 && forceLoginResponse.data) {
                message.success('登录成功');
                navigate('/games');
              }
            } catch (error) {
              message.error('登录失败，请重试');
            }
          }
        });
        return;
      }

      if (response.code === 200) {
        if (response.data === null) {
          setError('登录失败：无效的响应数据');
          return;
        }
        message.success('登录成功');
        navigate('/games');
      } else {
        setError(response.message || '登录失败');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('登录失败，请重试');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <MainContent>
        <ContentWrapper>
          <LoginSection>
            <Title level={2} style={{ textAlign: 'center', marginBottom: '30px', color: '#2d3748' }}>
              欢迎回来
            </Title>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
                closable
                onClose={() => setError(null)}
              />
            )}

            <StyledForm
              form={form}
              name="login"
              onFinish={handleLogin}
              size="large"
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '请输入用户名！' },
                  { min: 3, message: '用户名至少3个字符' }
                ]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="用户名"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码！' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  block
                  loading={loading}
                >
                  登录
                </Button>
              </Form.Item>

              <Divider plain></Divider>
            </StyledForm>
          </LoginSection>

          <ImageSection>
            <PromoText>
              <h2>超玥国象训练平台</h2>
              <p>超玥自主研发，业内独一无二</p>
              <p>专业的训练系统，只为超玥课程学员服务</p>
            </PromoText>
          </ImageSection>
        </ContentWrapper>
      </MainContent>

      <Footer>
        <div className="footer-content">
          <Space size={[40, 16]} wrap>
            <a href="http://www.chaoyuechess.cn/" target="_blank" rel="noopener noreferrer" className="footer-link">关于我们</a>
            <a href="https://www.chaoyuechess.com/" target="_blank" rel="noopener noreferrer" className="footer-link">超玥国象</a>
            <a href="https://appfllbnizi6634.h5.xiaoeknow.com/p/decorate/page/eyJpZCI6MzI1ODkwOCwiY2hhbm5lbF9pZCI6IiJ9" target="_blank" rel="noopener noreferrer" className="footer-link">热门课程</a>
            <Popover 
              content={
                <div style={{ textAlign: 'center' }}>
                  <img src="/contact.jpeg" alt="客服二维码" style={{ maxWidth: '200px', height: 'auto' }} />
                </div>
              }
              trigger="hover"
              placement="top"
            >
              <span className="footer-link" style={{ cursor: 'pointer' }}>客服咨询</span>
            </Popover>
          </Space>
          <div style={{ 
            marginTop: '12px', 
            opacity: 0.65,
            fontSize: '13px'
          }}>
            超玥国际象棋训练平台 ©2025 Created by ChaoYueChess
          </div>
        </div>
      </Footer>
    </LoginContainer>
  );
};

export default Login; 